import React, { useEffect } from "react";
import {
  Navigate,
  useParams,
  createBrowserRouter,
  Outlet,
  useNavigate,
} from "react-router-dom";
import ErrorBoundary from "./components/common/ErrorBoundary";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { GoogleOAuthProvider } from "@react-oauth/google";

// Layouts
import { MainLayout, AuthLayout } from "./components/layout";

// Guards
import { AuthGuard, GuestGuard } from "./components/common/AuthGuard";
import { TrialExpiryGuard } from "./components/common/TrialExpiryGuard";

// Auth Pages
import {
  LoginPage,
  SignupPage,
  ForgotPasswordPage,
  ConfirmEmailPage,
  ResetPasswordPage,
} from "./pages/auth";

// Dashboard Pages
import { DashboardPage } from "./pages/dashboard";

// Onboarding Pages
import { OnboardingPage } from "./pages/onboarding";

// Domain Pages
import {
  DomainsListPage,
  DomainDetailPage,
  ChatHistoryPage,
  UrlDiscoveryPage,
} from "./pages/domains";

// Subscription Pages
import { SubscriptionPage } from "./pages/subscription";

// Settings Pages
import { SettingsPage } from "./pages/settings";

// Contact Pages
import { ContactPage } from "./pages/contact";

// FAQ Pages
import { FAQPage } from "./pages/faq";

// Lead Pages
import { LeadListPage } from "./pages/leads";

// Notifications Pages
import { NotificationsPage } from "./pages/notifications";

// Admin Pages
import { AdminUserPage } from "./pages/admin";

// Error Pages
import { NotFoundPage } from "./pages/NotFoundPage";
import { ForbiddenPage } from "./pages/ForbiddenPage";
import { MaintenancePage } from "./pages/MaintenancePage";

// Routes
import { ROUTES } from "./config/routes";

// Store
import { useAuthStore } from "./stores/authStore";

// Components
import SessionExpiredModal from "./components/SessionExpiredModal";
import { registerNavigate } from "./services/navigation";
import {
  decodeJwtExp,
  _refreshWithMutex,
  handleRefreshFailure,
} from "./services/api";
import { cancelAllGuardedQueries } from "./lib/apiGuard/reactQueryIntegration";
import { WIDGET_URL, DOGFOOD_API_KEY } from './config/constants';
import { hideWidget, showWidget } from './lib/widgetVisibility';

// 定期refresh bounded再schedule定数 (EB-736)
const REFRESH_RETRY_MAX = 3;             // 有界リトライ上限(真失効確定まで)
const REFRESH_RETRY_INTERVAL_MS = 15000; // リトライ間隔(15秒)

// React Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (count, err) => {
        const status = (err as any)?.response?.status;
        if (status === 401 || status === 403) return false;
        return status >= 500 && count < 1;
      },
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function DomainDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/chatbots/${id}`} replace />;
}

function RootLayout() {
  const { setLoading, isSessionExpired } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(false);
  }, [setLoading]);

  useEffect(() => {
    registerNavigate(navigate);
  }, [navigate]);

  // proactive refresh: マウント時にトークン残り5分以内なら即時refresh (★critical-2: _doRefresh内で生axios使用)
  useEffect(() => {
    const { idToken, isAuthenticated } = useAuthStore.getState();
    if (!isAuthenticated || !idToken) return;
    const msRemaining = decodeJwtExp(idToken);
    if (msRemaining <= 5 * 60 * 1000) {
      useAuthStore.getState().setLoading(true); // AC3スピナー維持
      _refreshWithMutex()
        .catch(handleRefreshFailure)
        .finally(() => useAuthStore.getState().setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 定期refresh: exp-5分前に自動refresh (★critical-2: _doRefresh内で生axios使用)
  useEffect(() => {
    let pendingTimer: ReturnType<typeof setTimeout> | undefined;

    const scheduleRefresh = (retryCount = 0): void => {
      const { idToken, isAuthenticated } = useAuthStore.getState();
      if (!isAuthenticated || !idToken) return;
      const msRemaining = decodeJwtExp(idToken);
      const delay = Math.max(0, msRemaining - 5 * 60 * 1000);
      pendingTimer = setTimeout(async () => {
        try {
          await _refreshWithMutex();
          scheduleRefresh(0); // 成功: retryカウンタリセットして通常schedule復帰
        } catch (err: unknown) {
          const status = (err as any)?.response?.status;
          if (status === 401 || status === 403) {
            // 401/403: localStorage最新tokenで他タブrotation自動復帰を評価
            try {
              const raw = localStorage.getItem('entrybot_auth');
              if (raw) {
                const parsed = JSON.parse(raw);
                const freshIdToken: string | null = parsed?.state?.idToken ?? null;
                if (freshIdToken && decodeJwtExp(freshIdToken) > 5 * 60 * 1000) {
                  // 他タブがrotationした有効token → store最新化 + 通常schedule復帰(連鎖ロック自動復帰)
                  useAuthStore.getState().setTokens({
                    access_token: parsed.state.accessToken ?? '',
                    id_token: freshIdToken,
                    refresh_token: parsed.state.refreshToken ?? '',
                    expires_in: decodeJwtExp(freshIdToken),
                  });
                  useAuthStore.getState().setSessionExpired(false);
                  scheduleRefresh(0);
                  return;
                }
              }
            } catch { /* localStorage parse fail → fallthrough to retry */ }
            // 有効token無し: bounded再schedule(真失効判定猶予)
            if (retryCount < REFRESH_RETRY_MAX) {
              pendingTimer = setTimeout(() => scheduleRefresh(retryCount + 1), REFRESH_RETRY_INTERVAL_MS);
            } else {
              // N回使い切り: 真失効確定 → 手動再ログインへ
              handleRefreshFailure(err);
            }
          } else {
            // network/5xx: 従来通り(セッション維持)
            handleRefreshFailure(err);
          }
        }
      }, delay);
    };

    scheduleRefresh(0);
    return () => {
      if (pendingTimer) clearTimeout(pendingTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // セッション死遷移: isSessionExpired false→true で進行中クエリを物理キャンセル
  useEffect(() => {
    if (isSessionExpired) {
      cancelAllGuardedQueries(queryClient);
      hideWidget();
    } else {
      showWidget();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSessionExpired]);

  // タブ復帰時proactive refresh: visibilitychange/focus復帰でトークン期限確認
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState !== "visible") return;
      const { idToken, isAuthenticated, isSessionExpired } =
        useAuthStore.getState();
      if (isSessionExpired) return;
      if (!isAuthenticated || !idToken) return;
      if (decodeJwtExp(idToken) <= 5 * 60 * 1000) {
        _refreshWithMutex().catch(handleRefreshFailure);
      }
    };
    document.addEventListener("visibilitychange", handler);
    window.addEventListener("focus", handler);
    return () => {
      document.removeEventListener("visibilitychange", handler);
      window.removeEventListener("focus", handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // dogfood widget初期化: DOGFOOD_API_KEY設定済み環境でのみwidget.jsを動的ロード
  useEffect(() => {
    if (!DOGFOOD_API_KEY) return;
    const script = document.createElement('script');
    script.src = `${WIDGET_URL}/widget.js`;
    script.onload = () => {
      (window as any).EntryBot?.init({
        apiKey: DOGFOOD_API_KEY,
        position: 'bottom-right',
      });
    };
    document.head.appendChild(script);
  }, []);

  return (
    <ErrorBoundary>
      <GoogleOAuthProvider
        clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ""}
      >
        <QueryClientProvider client={queryClient}>
          {isSessionExpired && <SessionExpiredModal />}
          <Outlet />
        </QueryClientProvider>
      </GoogleOAuthProvider>
    </ErrorBoundary>
  );
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: <RootLayout />,
    children: [
      // 認証ページ（GuestGuard + AuthLayout）
      {
        element: (
          <GuestGuard>
            <AuthLayout />
          </GuestGuard>
        ),
        children: [
          { path: ROUTES.LOGIN, element: <LoginPage /> },
          { path: ROUTES.SIGNUP, element: <SignupPage /> },
          { path: ROUTES.FORGOT_PASSWORD, element: <ForgotPasswordPage /> },
          { path: ROUTES.RESET_PASSWORD, element: <ResetPasswordPage /> },
          { path: ROUTES.CONFIRM_EMAIL, element: <ConfirmEmailPage /> },
        ],
      },
      // メインアプリケーション（AuthGuard + MainLayout）
      {
        element: (
          <AuthGuard>
            <TrialExpiryGuard>
              <MainLayout />
            </TrialExpiryGuard>
          </AuthGuard>
        ),
        children: [
          { path: ROUTES.ONBOARDING, element: <OnboardingPage /> },
          { path: ROUTES.HOME, element: <DashboardPage /> },
          { path: ROUTES.NOTIFICATIONS, element: <NotificationsPage /> },
          { path: ROUTES.DOMAINS, element: <DomainsListPage /> },
          { path: ROUTES.DOMAIN_NEW, element: <OnboardingPage /> },
          { path: ROUTES.DOMAIN_DISCOVER, element: <UrlDiscoveryPage /> },
          { path: ROUTES.DOMAIN_DETAIL, element: <DomainDetailPage /> },
          { path: ROUTES.DOMAIN_CHAT_HISTORY, element: <ChatHistoryPage /> },
          { path: "/domains", element: <Navigate to="/chatbots" replace /> },
          {
            path: "/domains/new",
            element: <Navigate to="/chatbots/new" replace />,
          },
          { path: "/domains/:id", element: <DomainDetailRedirect /> },
          { path: ROUTES.SUBSCRIPTION, element: <SubscriptionPage /> },
          { path: ROUTES.SETTINGS, element: <SettingsPage /> },
          { path: ROUTES.SETTINGS_WITHDRAWAL, element: <SettingsPage /> },
          { path: ROUTES.LEADS, element: <LeadListPage /> },
          { path: ROUTES.ADMIN_USERS, element: <AdminUserPage /> },
          { path: ROUTES.CONTACT, element: <ContactPage /> },
          { path: ROUTES.FAQ, element: <FAQPage /> },
          { path: "/forbidden", element: <ForbiddenPage /> },
          { path: "*", element: <NotFoundPage /> },
        ],
      },
      // メンテナンスページ（レイアウト外）
      { path: "/maintenance", element: <MaintenancePage /> },
    ],
  },
]);
